/* eslint-disable */
import React, { useState, useEffect } from "react";
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Badge,
  Box,
  IconButton,
  Icon,
  useColorModeValue} from "@chakra-ui/react";
import { FaEnvelope, FaTrash, FaCopy, FaExternalLinkAlt, FaSyncAlt } from "react-icons/fa";
import { useModalToast } from "../../context/GlobalModalToast";

export default function EmailOutboxDrawer({ isOpen, onClose, email: emailProp }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useModalToast();

  // Dynamic theme tokens
  const drawerBg = useColorModeValue("white", "gray.900");
  const headerBg = useColorModeValue("purple.50", "rgba(107, 70, 193, 0.15)");
  const headerColor = useColorModeValue("purple.800", "purple.200");
  const bodyBg = useColorModeValue("gray.50", "gray.800");
  const cardBg = useColorModeValue("white", "gray.700");
  const cardBorder = useColorModeValue("gray.200", "gray.600");
  const textMain = useColorModeValue("gray.700", "gray.100");
  const textSub = useColorModeValue("gray.600", "gray.300");
  const textMuted = useColorModeValue("gray.500", "gray.400");
  const emptyIconColor = useColorModeValue("gray.300", "gray.500");
  const emptyTextColor = useColorModeValue("gray.400", "gray.400");
  const otpBg = useColorModeValue("purple.50", "rgba(107, 70, 193, 0.2)");
  const otpBorder = useColorModeValue("purple.100", "rgba(107, 70, 193, 0.4)");
  const otpLabel = useColorModeValue("purple.500", "purple.300");
  const otpCode = useColorModeValue("purple.800", "purple.200");
  const bodyPreviewBg = useColorModeValue("gray.50", "gray.800");
  const bodyPreviewBorder = useColorModeValue("gray.300", "gray.500");

  const userObj = JSON.parse(localStorage.getItem('user')) || {};
  const targetEmail = emailProp || userObj.email || userObj.user?.email || "";

  const fetchLogs = async () => {
    if (!targetEmail) {
      setLogs([]);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/email-logs?email=${encodeURIComponent(targetEmail)}`);
      const data = await res.json();
      setLogs(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchLogs();
    }
  }, [isOpen, targetEmail]);

  const handleClear = async () => {
    if (!targetEmail) return;
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/api/email-logs/clear?email=${encodeURIComponent(targetEmail)}`, { method: "DELETE" });
      setLogs([]);
      toast({
        title: "Logs Cleared",
        status: "success",
        duration: 2000,
        isClosable: true});
    } catch (err) {
      console.error(err);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "OTP Copied",
      description: `Copied ${text} to clipboard!`,
      status: "success",
      duration: 1500,
      isClosable: true});
  };

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
      <DrawerOverlay backdropFilter="blur(5px)" />
      <DrawerContent bg={drawerBg} borderLeftRadius="xl" boxShadow="2xl">
        <DrawerCloseButton color={textMain} />
        <DrawerHeader borderBottomWidth="1px" borderColor={cardBorder} bg={headerBg} color={headerColor} borderTopLeftRadius="xl">
          <HStack justify="space-between" pr={6}>
            <HStack>
              <Icon as={FaEnvelope} w={5} h={5} color="purple.500" />
              <Text fontWeight="800" fontSize="lg" letterSpacing="-0.5px">SkillUp Simulated Outbox</Text>
            </HStack>
            <HStack spacing={2}>
              <IconButton
                aria-label="Refresh"
                icon={<FaSyncAlt />}
                size="sm"
                variant="ghost"
                color={textSub}
                onClick={fetchLogs}
                isLoading={loading}
              />
              <IconButton
                aria-label="Clear All"
                icon={<FaTrash />}
                size="sm"
                colorScheme="red"
                variant="ghost"
                onClick={handleClear}
                disabled={logs.length === 0}
              />
            </HStack>
          </HStack>
        </DrawerHeader>

        <DrawerBody p={4} bg={bodyBg}>
          <VStack spacing={4} align="stretch">
            <Text fontSize="xs" color={textMuted} fontStyle="italic">
              Every OTP code, invite, or SSO code sent by the SkillUp system is simulated here in real-time. Use these logs for easy test flows!
            </Text>

            {!targetEmail ? (
              <VStack py={20} spacing={4} justify="center">
                <Icon as={FaEnvelope} w={12} h={12} color={emptyIconColor} />
                <Text color={emptyTextColor} fontWeight="bold">No Email Specified</Text>
                <Text fontSize="xs" color={emptyTextColor} textAlign="center" px={10}>
                  Please log in or enter your email address to view the simulated emails generated specifically for you.
                </Text>
              </VStack>
            ) : logs.length === 0 ? (
              <VStack py={20} spacing={4} justify="center">
                <Icon as={FaEnvelope} w={12} h={12} color={emptyIconColor} />
                <Text color={emptyTextColor} fontWeight="bold">Outbox is empty</Text>
                <Text fontSize="xs" color={emptyTextColor} textAlign="center" px={10}>
                  Generate a Password Reset code, log in via SSO, or Invite a student to a course to see them populate live!
                </Text>
              </VStack>
            ) : (
              logs.map((log) => (
                <Box
                  key={log._id}
                  p={4}
                  bg={cardBg}
                  borderRadius="xl"
                  borderWidth="1px"
                  borderColor={cardBorder}
                  boxShadow="sm"
                  transition="all 0.2s"
                  _hover={{ boxShadow: "md", borderColor: "purple.400" }}
                >
                  <HStack justify="space-between" mb={2}>
                    <Badge
                      colorScheme={
                        log.type === "sso"
                          ? "cyan"
                          : log.type === "forgot-password"
                          ? "orange"
                          : "purple"
                      }
                      borderRadius="full"
                      px={2.5}
                      py={0.5}
                      fontSize="2xs"
                      textTransform="uppercase"
                    >
                      {log.type}
                    </Badge>
                    <Text fontSize="2xs" color={textMuted}>
                      {new Date(log.createdAt).toLocaleTimeString()}
                    </Text>
                  </HStack>

                  <Text fontSize="sm" fontWeight="800" color={textMain} mb={1}>
                    To: {log.to}
                  </Text>
                  <Text fontSize="xs" fontWeight="500" color={textSub} mb={3}>
                    Subject: {log.subject}
                  </Text>

                  {log.otp && (
                    <Box
                      p={3}
                      mb={3}
                      bg={otpBg}
                      borderRadius="lg"
                      borderWidth="1px"
                      borderColor={otpBorder}
                    >
                      <HStack justify="space-between">
                        <Box>
                          <Text fontSize="3xs" textTransform="uppercase" color={otpLabel} fontWeight="700">
                            Verification OTP Code
                          </Text>
                          <Text fontSize="xl" fontWeight="900" color={otpCode} letterSpacing="1px">
                            {log.otp}
                          </Text>
                        </Box>
                        <Button
                          size="xs"
                          leftIcon={<FaCopy />}
                          colorScheme="purple"
                          variant="solid"
                          onClick={() => copyToClipboard(log.otp)}
                        >
                          Copy Code
                        </Button>
                      </HStack>
                    </Box>
                  )}

                  <Box fontSize="xs" bg={bodyPreviewBg} color={textSub} p={3} borderRadius="lg" maxH="120px" overflowY="auto" mb={3} borderStyle="dashed" borderWidth="1px" borderColor={bodyPreviewBorder}>
                    <div dangerouslySetInnerHTML={{ __html: log.body }} />
                  </Box>

                  {log.previewUrl && (
                    <Button
                      as="a"
                      href={log.previewUrl}
                      target="_blank"
                      size="xs"
                      width="full"
                      leftIcon={<FaExternalLinkAlt />}
                      colorScheme="teal"
                      variant="outline"
                    >
                      Open Live HTML Email Preview
                    </Button>
                  )}
                </Box>
              ))
            )}
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
